import React, { useEffect, useState, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { trackJobView, trackCompanyLink, trackJobApply, trackEvent } from '../../../../utils/analytics/analytics';
import type { AnalyticsEvent } from '../../../../types/analytics-events';
import { CaretDown, CaretUp, Info, MapPin, WifiHigh, CheckCircle } from '@phosphor-icons/react';
import { localisedRouteGenerator } from '../../../../i18n/routes/routeGenerator';
import { generateJobUrl } from '../../../../utils/routing/generate-url';
import { BASE_URL, LOCALIZED_PATHS } from '../../../../utils/constants/url-constants';
import { getDevSiteOrigin } from '../../../../utils/constants/dev-site-origin';
import { Link } from '../../../../components/Link';
import { JobDetailAlertCta } from '../../../../components/job-alerts/JobDetailAlertCta';
import type { DisplayJob } from '../../../../types';
import {
  resolveJobDisplayDescriptionFromDisplayJob,
} from '../../../../utils/job/pick-job-display-description';
import type { FinalJob } from '../../../../lambda/types';
import { generateJobPostingSchema } from '../../../../utils/seo/generate-job-posting-schema';
// normalizeJobDetail is now used by useJobNormalization hook
import { generateCanonicalUrls } from '../../../../utils/seo/generate-canonical-urls';
import { useData } from "vike-react/useData";
import { usePageContext } from "vike-react/usePageContext";
import type { Data } from "./+data.js";
import { useAppSelector } from '../../../../redux/hooks';
import { ApplicationStateSelector } from '../../../../components/ApplicationStateSelector';
import { APPLICATION_STATES } from '../../../../components/application-state';
import { useToast } from '../../../../contexts/ToastContext';
import { getIndexName } from '../../../../constants';
import type { HubCrossLink } from '../../../../components/hub/HubCrossLinks';
import { fixMojibake } from '../../../../utils/text/fix-mojibake';
import { CompanyName } from '../../../../components/CompanyName';
import { useUserJobStateSync } from '../../../../hooks/useUserJobStateSync';
import { useUserState } from '../../../../query/UserProvider';
import type { ResolvedJobMatch } from '../../../../types/job-match';
import { resolveJobMatchScore } from '../../../../utils/job-match/resolve-job-match-score';
import { useAuth } from '../../../../hooks/useAuth';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { adjustBrandColor } from '../../../../utils/brand-color/adjust-brand-color';
import { resolveEffectiveBrandColour } from '../../../../utils/company/resolve-effective-brand-colour';
import { resolveSearchHitIsTyomarkkinatori } from '../../../../utils/search/resolve-search-hit-is-tyomarkkinatori';
import { APPLY_AUTOFILL_COOKIE } from '../../../../utils/feature-flags/apply-autofill-flag';
import { formatDate, calculateEstimatedExpiryDate, calculateDaysAgo } from './utils/dateUtils';
import { isDark, isWhiteOrNearWhite } from './utils/jobDetailUtils';
import { transformJobForLanguage, getIconForItem, scrollToSection, getFirstCompanyUrl, extractYouTubeVideoId, isValidNumber, safeParseNumber, hasAnyValidNumber, getValidNumberOrNull, calculateCompanyAge, formatCompanyAge, getSocialIcon, calculateBrandStyles, generateMetaDescription, generateJobKeywords, getFirstConcreteLocationSegment } from './utils';
import { convertSalaryToMonthlyEUR, hasOriginalSalary, getCurrencySymbol, type OriginalSalary } from './utils/salaryConversion';
import { estimateFinnishNetSalary } from './utils/estimateFinnishNetSalary';
import { encodeTaxIcebergUrl, DEFAULT_TAX_ICEBERG_STATE } from '../../../../utils/salary/tax-iceberg-url';
import { readChartsCollapseCookie, writeChartsCollapseCookie } from './utils/chartsCollapse';
import { calculateHasAnyCharts } from './utils/chartsUtils';
import { CompanyLogo, TabContent, MoneyGraph, MonthlyHiringChart, StarburstChart, SocialMediaButtons, LocationRemotenessChart, SalaryStatisticsChart, RoleRoleLevelChart, DonutSunburstChart, CompanyDistributionChart, LanguageSwitcher, LanguagesSpecialisationsSection, EllipsesMenuDropdown, JobDescriptionSection, JobNotFound404, JobDetailSkeleton, TyomarkkinatoriDataSourceAttribution, TailorCvModal, AutofillApplyModal } from './components';
import { BreadcrumbNavigation } from '../../../../components/BreadcrumbNavigation';
import { sharePage } from './utils/shareUtils';
import { useDebugRenderIf } from '../../../../utils/debug/debug-render';
import { useJobState } from './hooks/useJobState';
import { useJobActions } from './hooks/useJobActions';
import { useJobNormalization } from './hooks/useJobNormalization';
import { useLanguageUrls } from './hooks/useLanguageUrls';
import { JobDetailPageV2 } from './v2/JobDetailPageV2';
import { useSalaryDisplay } from './hooks/useSalaryDisplay';
import { resolveJobPageBreadcrumbModel } from './utils/resolve-job-page-breadcrumb';
import { resolveJobDetailData } from './utils/resolveJobDetailData';
import { fetchSimilarJobsFromAlgolia } from '../../../../utils/job-detail/similar-jobs-algolia';
import { buildSimilarJobsSrpHref } from './utils/buildSimilarJobsSrpHref';
import { resolveJobSalaryListingProvenance } from '../../../../utils/job-detail/resolve-job-salary-listing-provenance';
import {
  resolveJobUrlCompany,
  resolveJobUrlLocation,
  resolveJobUrlRemoteness,
  resolveJobUrlTitle,
} from '../../../../utils/routing/job-url-input';

import { EngagementStrip } from '../../../../components/engagement/EngagementStrip';
import { useFormatMessage } from '../../../../i18n-review/client/Msg';
import { useNewLocationSystemEnabled } from '../../../../utils/facets/new-location-system-dev-flag';
import {
  formatStructuredJobLocationLine,
  resolveStructuredJobLocations,
  resolveStructuredRemoteScopes,
} from '../../../../utils/location/structured-job-location-display';

//TODO:: Fetch object id from all 3 indices so I can generate all 3 urls

function CheckCircleSolidApplied({ className }: { className?: string }) {
  return <CheckCircle weight="fill" className={className} />;
}

/** Read serialized page context from window so first client paint has data and matches server (avoids hydration mismatch). */
function getSerializedData(): Data | undefined {
  if (typeof window === 'undefined') return undefined;
  const pc = (window as { __VITE_PAGE_CONTEXT__?: { data?: Data } }).__VITE_PAGE_CONTEXT__;
  return pc?.data;
}

export interface JobDetailPageProps {
  /** When mounted in the job island (static→interactive swap), data is passed in; no useData. */
  initialData?: Data;
  /** Locale when mounted in island (no usePageContext). */
  initialLocale?: string;
}

const JobDetailPage: React.FC<JobDetailPageProps> = ({ initialData, initialLocale }) => {
  const dataFromHook = useData<Data>();
  const pageContext = usePageContext();
  const jobShareBaseUrl = import.meta.env.DEV
    ? getDevSiteOrigin({
        requestUrl:
          typeof (pageContext as { urlOriginal?: string } | undefined)?.urlOriginal === "string"
            ? (pageContext as { urlOriginal: string }).urlOriginal
            : undefined,
      })
    : BASE_URL;
  // Use serialized data on first client paint so we match server output and avoid hydration crash (useData/pageContext can be delayed).
  // When mounted in the job island, initialData is provided.
  const data = useMemo(
    () =>
      resolveJobDetailData([
        initialData,
        dataFromHook,
        (pageContext as { data?: Data } | undefined)?.data,
        getSerializedData(),
      ]),
    [initialData, dataFromHook, pageContext],
  );
  // Guard: during client-side navigation pageContext can be briefly undefined; avoid reading routeParams until ready.
  // When in island, derive from initialData.job.
  const routeParams = pageContext?.routeParams as { jobId?: string; companyId?: string; seoSlug?: string } | undefined;
  const routeParamsFromJob =
    initialData?.job && typeof initialData.job === 'object' && initialData.job !== null
      ? {
          jobId: String((initialData.job as { objectID?: unknown }).objectID ?? ''),
          companyId: String((initialData.job as { companyId?: unknown }).companyId ?? (initialData.job as { company?: unknown }).company ?? ''),
          seoSlug: (initialData as { seoSlug?: string }).seoSlug ?? '',
        }
      : undefined;
  const effectiveRouteParams = routeParams ?? routeParamsFromJob;
  const seoSlug = effectiveRouteParams?.seoSlug;
  // Safely destructure data - it can be undefined during client-side navigation transitions
  const { job, httpStatusCode, heroImage: serverHeroImage, heroImageHasTransparency, hasAnyCharts: serverHasAnyCharts, similarJobs, liveOpenRolesCount, companyEmployerScore } = data || {
    job: undefined,
    httpStatusCode: undefined,
    heroImage: undefined,
    heroImageHasTransparency: undefined,
    hasAnyCharts: false,
    similarJobs: [],
    liveOpenRolesCount: null,
    companyEmployerScore: null,
  };
  const intl = useIntl();
  const formatMessage = useFormatMessage();

  // Use pageContext.locale for SSR compatibility (available during SSR), fallback to intl.locale for client. In island, use initialLocale.
  const jobLocale = (initialLocale ?? pageContext?.locale ?? intl.locale ?? 'en') as string;

  // Use extracted useJobNormalization hook
  const { displayJob, finalJob, normalizationMeta } = useJobNormalization({
    job,
    jobLocale,
    seoSlug,
  });

  // TEC-482: with the new location system on, the headline location line and the brand rail show
  // the job's gazetteer-resolved places (per-locale labels, e.g. Helsingfors in sv) and the remote
  // scope rides with the work model ("Etätyö (EMEA)") instead of appearing as a "Remote - EMEA"
  // pseudo-location. Both resolve to null (legacy strings keep rendering) when the flag is off or
  // the job predates TEC-482 and hasn't been backfilled. Flag starts false during SSR/hydration
  // and flips post-mount, same as every other flag-gated surface.
  const newLocationSystemEnabled = useNewLocationSystemEnabled();
  const structuredLocale = jobLocale === 'fi' || jobLocale === 'sv' ? jobLocale : 'en';
  const structuredJobLocation = useMemo(
    () =>
      newLocationSystemEnabled
        ? resolveStructuredJobLocations(displayJob, { locale: structuredLocale, hideForeignWhenFinlandPresent: false })
        : null,
    [newLocationSystemEnabled, displayJob, structuredLocale],
  );
  const structuredRemoteScopes = useMemo(
    () => (newLocationSystemEnabled ? resolveStructuredRemoteScopes(displayJob, structuredLocale) : []),
    [newLocationSystemEnabled, displayJob, structuredLocale],
  );

  const [clientSimilarJobs, setClientSimilarJobs] = useState<Data['similarJobs'] | null>(null);
  const similarJobsFetchAttemptedRef = React.useRef<Set<string>>(new Set());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const objectId = String((displayJob as { objectID?: unknown } | undefined)?.objectID ?? '');
    if (!objectId) return;

    const hasServerSimilarJobs = Array.isArray(similarJobs) && similarJobs.length > 0;
    if (hasServerSimilarJobs) {
      setClientSimilarJobs(null);
      return;
    }
    if (similarJobsFetchAttemptedRef.current.has(objectId)) return;

    similarJobsFetchAttemptedRef.current.add(objectId);
    const primaryLocation = getFirstConcreteLocationSegment(displayJob?.location);
    const locale = (['en', 'fi', 'sv'].includes(jobLocale) ? jobLocale : 'en') as 'en' | 'fi' | 'sv';

    fetchSimilarJobsFromAlgolia({
      jobObjectId: objectId,
      normalisedTitle: (displayJob as { normalisedTitle?: string } | undefined)?.normalisedTitle,
      primaryLocation,
      jobLocale: locale,
      indexName: getIndexName(locale),
      baseUrl: window.location.origin,
    })
      .then(({ similarJobs: fetchedSimilarJobs }) => {
        setClientSimilarJobs(Array.isArray(fetchedSimilarJobs) ? fetchedSimilarJobs : []);
      })
      .catch(() => {
        setClientSimilarJobs([]);
      });
  }, [displayJob, jobLocale, similarJobs]);

  const effectiveSimilarJobs = useMemo(() => {
    const sourceSimilarJobs = Array.isArray(similarJobs) ? similarJobs : [];
    if (sourceSimilarJobs.length > 0) return sourceSimilarJobs;
    return Array.isArray(clientSimilarJobs) ? clientSimilarJobs : sourceSimilarJobs;
  }, [similarJobs, clientSimilarJobs]);

  const similarJobsSeeAllHref = useMemo(() => {
    const primaryLocation = getFirstConcreteLocationSegment(displayJob?.location);
    return buildSimilarJobsSrpHref({
      locale: jobLocale,
      normalisedTitle: displayJob?.normalisedTitle || displayJob?.title || '',
      primaryLocation,
    });
  }, [displayJob, jobLocale]);

  // JobDetailAlertCta's role/location match what the page's own header actually displays
  // (getFirstConcreteLocationSegment, same as the `location` prop passed to JobDetailPageV2
  // below), not similarJobsSeeAllHref's raw first-array-element location, so the CTA's own
  // copy ("...jobs in {location}") and the search it actually sets up never disagree.
  const jobDetailAlertRole =
    ((displayJob as Record<string, unknown> | undefined)?.[`normalisedTitle_${jobLocale}`] as string | undefined) ||
    displayJob?.normalisedTitle ||
    displayJob?.title ||
    '';
  const jobDetailAlertPrimaryLocation = getFirstConcreteLocationSegment(displayJob?.location) || undefined;
  const jobDetailAlertTargetUrl = useMemo(
    () =>
      buildSimilarJobsSrpHref({
        locale: jobLocale,
        normalisedTitle: jobDetailAlertRole,
        primaryLocation: jobDetailAlertPrimaryLocation,
      }),
    [jobLocale, jobDetailAlertRole, jobDetailAlertPrimaryLocation]
  );

  // Charts collapse state - read from cookie on mount (client-side only)
  const [chartsCollapsed, setChartsCollapsed] = useState<boolean>(false);

  useEffect(() => {
    // Read initial state from cookie (client-side only)
    const cookieValue = readChartsCollapseCookie();
    if (cookieValue !== null) {
      setChartsCollapsed(cookieValue);
    }
  }, []);

  const toggleChartsCollapse = () => {
    const newState = !chartsCollapsed;
    setChartsCollapsed(newState);
    writeChartsCollapseCookie(newState);
  };

  // Original job text display state
  const [showOriginalJobText, setShowOriginalJobText] = useState<boolean>(false);

  // Company name copy state
  const [companyNameCopied, setCompanyNameCopied] = useState<boolean>(false);

  // Use extracted useJobState hook
  const { jobId, isSaved, isApplied, isHidden, currentApplicationState, isToolsPage } = useJobState({ displayJob });

  // Get toast and other dependencies
  const { showToast } = useToast();
  const { isAuthenticated, signIn } = useAuth();
  const applyAutofillEnabled = useFeatureFlag(APPLY_AUTOFILL_COOKIE);
  const userProfile = useAppSelector((state) => state.jobForm);
  const comparisonPreferences = useAppSelector((state) => state.comparisonPreferences);
  const savedSearches = useAppSelector((state) => state.recentSearches?.savedSearches ?? []);
  const { recordViewedJob, setAppliedJobState } = useUserJobStateSync();

  // Kept subscribed to userState changes (re-renders this component when it updates) even though
  // match score no longer reads its value directly; see isHydrated below.
  useUserState();
  // jobForm/comparisonPreferences (read via useAppSelector above) are always synchronously final
  // by the time this renders: createStore.ts's applySsrStateHydration resolves them up front, from
  // real userState or; for anonymous visitors, who have no userState; the matchProfile cookie
  // fallback (utils/shared/client-preferences.ts). There's no async gap to wait out the way there
  // is client-side for a first-time TanStack userState fetch, so match score never needs to be
  // withheld pending a "hydrated" signal.
  const isHydrated = true;

  const breadcrumbSearchReturnHref = null;

  // Generate job URL for share functionality
  const jobUrl = useMemo(() => {
    if (!displayJob) return '';
    // objectID is normally == pk, but some rows are missing it (see getJob.ts/processor.ts);
    // fall back to the jobId already in the URL rather than crashing SSR.
    const resolvedJobId = displayJob.objectID ?? effectiveRouteParams?.jobId;
    if (!resolvedJobId) return '';
    return generateJobUrl({
      baseUrl: jobShareBaseUrl,
      jobId: resolvedJobId.toString(),
      locale: intl.locale as any,
      title: resolveJobUrlTitle(displayJob),
      company: resolveJobUrlCompany(displayJob),
      location: resolveJobUrlLocation(displayJob),
      remoteness: resolveJobUrlRemoteness(displayJob),
    });
  }, [displayJob, intl.locale, jobShareBaseUrl, effectiveRouteParams?.jobId]);

  // Use extracted useJobActions hook
  const { handleToggleSaved, handleToggleApplied, handleToggleHide, handleSetApplicationState, handleShare } = useJobActions({
    displayJob,
    jobId,
    isSaved,
    isApplied,
    isHidden,
    showToast,
    url: jobUrl,
  });

  // Ellipses menu state
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!menuRef.current?.contains(target)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMenuOpen]);

  // Get the state to display in the ellipsis button
  const ellipsisButtonState = React.useMemo(() => {
    if (currentApplicationState) {
      const stateConfig = APPLICATION_STATES.find(s => s.value === currentApplicationState);
      return stateConfig ? {
        label: formatMessage({ id: stateConfig.labelKey, defaultMessage: stateConfig.defaultLabel }),
        icon: stateConfig.iconSolid,
        color: stateConfig.color,
      } : null;
    }
    if (isApplied) {
      return {
        label: formatMessage({ id: 'tools.applicationState.applied', defaultMessage: 'Applied', description: 'Application state label indicating job has been applied to' }),
        icon: CheckCircleSolidApplied,
        color: 'blue',
      };
    }
    return null;
  }, [currentApplicationState, isApplied, intl]);

  // Refs for scrolling to sections
  const jobDetailsRef = React.useRef<HTMLDivElement>(null);

  // Debug: Track renders and navigation state
  useDebugRenderIf('JobDetailPage', {
    jobId: displayJob?.objectID,
    hasJob: !!displayJob,
    locale: jobLocale,
  });

  // Match score: profile when filled; otherwise infer from persisted recent searches
  const resolvedJobMatch = React.useMemo((): ResolvedJobMatch => {
    if (!displayJob || !isHydrated) {
      return { breakdown: null, source: null, searchInferenceBullets: [] };
    }
    return resolveJobMatchScore({
      job: displayJob,
      userProfile,
      comparisonPreferences,
      savedSearches,
      isHydrated,
    });
  }, [displayJob, isHydrated, userProfile, comparisonPreferences, savedSearches]);

  const matchScoreData = resolvedJobMatch.breakdown;
  const matchScoreMeta =
    resolvedJobMatch.source === 'recent-searches'
      ? ({ source: 'recent-searches' as const, bullets: resolvedJobMatch.searchInferenceBullets })
      : null;

  // const routerLocation = useLocation(); // Commented out react-router-dom
  // Get sendEvent from location state if passed from Hit
  // const sendEvent = routerLocation.state?.sendEvent; // Commented out

  const [showTailorCvModal, setShowTailorCvModal] = useState(false);
  const [showAutofillApplyModal, setShowAutofillApplyModal] = useState(false);
  const [tailorModalInitialTab, setTailorModalInitialTab] = useState<'cv' | 'letter'>('cv');
  const handleOpenTailorCv = React.useCallback(
    (opts?: { source?: 'job_page' | 'saved_jobs' | 'deep_link' | 'match_score'; tab?: 'cv' | 'letter' }) => {
      trackEvent({
        name: 'tailor_cv_interaction',
        params: {
          action: 'open',
          job_id: displayJob?.objectID,
          company_id: displayJob?.companyId ?? undefined,
          locale: intl.locale,
          source: opts?.source ?? 'job_page',
        },
      });
      setTailorModalInitialTab(opts?.tab ?? 'cv');
      setShowTailorCvModal(true);
    },
    [displayJob, intl.locale]
  );

  // Deep link (?tailor=cv|letter) auto-opens the modal after the island mounts; job pages are an
  // island (useData()/pageContext are unreliable client-side here), so this reads location.search
  // directly. Strips the param via replaceState so a refresh/share doesn't refire it.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const tailorParam = params.get('tailor');
    if (tailorParam !== 'cv' && tailorParam !== 'letter') return;

    handleOpenTailorCv({ source: 'deep_link', tab: tailorParam });

    params.delete('tailor');
    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
    window.history.replaceState(null, '', nextUrl);
    // Deliberately runs only once per mount; re-checking location.search on every dependency
    // change would refire the deep link after the param has already been stripped.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [showCompanyContactDetails, setShowCompanyContactDetails] = useState(false);
  const [showCompanyLocationMap, setShowCompanyLocationMap] = useState(false);
  const [companyInfoActiveTab, setCompanyInfoActiveTab] = useState<
    'financial' | 'hiring' | 'roles' | 'locations' | 'languages' | 'map'
  >('financial');
  const [showOriginalSalary] = useState(false);

  // Calculate estimated expiry date if applicationDeadline is missing (35 days from postedDate)
  // This hook must be called before any early returns to follow Rules of Hooks
  const estimatedExpiryDate = React.useMemo(() => {
    if (!displayJob) return null;
    return calculateEstimatedExpiryDate(displayJob.applicationDeadline, displayJob.postedDate);
  }, [displayJob]);

  // Track salary insight view (once per job)
  const salaryInsightTrackedRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (displayJob?.objectID && displayJob.objectID !== salaryInsightTrackedRef.current) {
      const hasSalaryInsight =
        typeof displayJob.salaryRangeMin === 'number' &&
        typeof displayJob.salaryRangeMax === 'number' &&
        displayJob.salaryRangeMin > 0 &&
        displayJob.salaryRangeMax > 0;

      if (hasSalaryInsight) {
        const listingProv = resolveJobSalaryListingProvenance({
          salaryRangeMin: displayJob.salaryRangeMin,
          salaryRangeMax: displayJob.salaryRangeMax,
          salaryDetails: displayJob.salaryDetails,
        });
        const hasExactSalary = listingProv === 'employer_quote';
        const hasEstimatedSalary = listingProv !== 'employer_quote';

        trackEvent({
          name: 'salary_insight_view',
          params: {
            job_id: displayJob.objectID,
            company_id: displayJob.companyId || '',
            has_exact_salary: hasExactSalary,
            has_estimated_salary: hasEstimatedSalary,
            locale: intl.locale,
          },
        });

        salaryInsightTrackedRef.current = displayJob.objectID;
      }
    }
  }, [displayJob, intl.locale]);

  // Effect to handle side effects (tracking, etc.) - only run once per job
  const jobViewTrackedRef = React.useRef<string | null>(null);

  useEffect(() => {
    // Handle side effects (tracking, etc.) - only run once per job
    if (job?.objectID && displayJob) {
      void recordViewedJob({ id: job.objectID, ...displayJob });

      // Track job view with typed event. Guarded by a ref (not just the effect deps) because
      // `recordViewedJob`'s identity changes whenever ANY unrelated userState entry is written
      // (profile sync, other jobs' browsed/saved/applied writes, etc.) via its `userStateEntries`
      // dependency in useUserJobStateSync; without this guard the effect re-fires job_view on
      // every such unrelated write instead of once per job, wildly inflating the GA4 count.
      if (job.objectID !== jobViewTrackedRef.current) {
        const hasSalaryInsight =
          typeof displayJob.salaryRangeMin === 'number' &&
          typeof displayJob.salaryRangeMax === 'number' &&
          displayJob.salaryRangeMin > 0 &&
          displayJob.salaryRangeMax > 0;

        const locationLabel = getFirstConcreteLocationSegment(displayJob.location) || undefined;

        trackEvent({
          name: 'job_view',
          params: {
            job_id: job.objectID,
            job_title: displayJob.normalisedTitle || displayJob.title || '',
            company_id: displayJob.companyId || '',
            location_label: locationLabel,
            is_remote: displayJob.remoteness === 'remote' || displayJob.remoteness === 'hybrid',
            has_salary_insight: hasSalaryInsight,
            locale: intl.locale,
          },
        });

        jobViewTrackedRef.current = job.objectID;
      }
    }
  }, [displayJob, job?.objectID, recordViewedJob, intl.locale]);


  // No data from useData() or pageContext yet: show skeleton (avoid blank page on client nav / delayed hydration).
  const hasData = data != null;
  const hasJob = job != null && job !== undefined;
  if (!hasData && effectiveRouteParams?.jobId) {
    return <JobDetailSkeleton />;
  }

  // Data has loaded but job is missing: show not found only when we're sure (data resolved, no job).
  if (hasData && !hasJob) {
    return <JobNotFound404 />;
  }

  // Still waiting for displayJob normalization: render nothing to avoid skeleton CLS.
  if (hasJob && (displayJob === undefined || displayJob === null)) {
    return null;
  }

  if (!displayJob) {
    return null;
  }

  const {
    title,
    description,
    originalDescription,
    postedDate,
    lastSeen,
    field,
    jobType,
    location,
    remoteness,
    companyId,
    requiredLanguages,
    optionalLanguages,
    salaryRangeMin,
    salaryRangeMax,
    salaryPeriod,
    salaryDetails,
    currency,
    peopleManagementTier,
    specialisations,
    applicationDeadline,
    applyRedirectPath,
    normalisedRoleLevel,
    normalisedTitle,
    brandColour,
    companyIndustry,
    employerOverview,
    companyCoreValues,
    companyAwardsAndCertifications,
    companyAnnualRevenueRangeMin,
    companyAnnualRevenueRangeMax,
    officeImages,
    socialMediaLinks,
    minimumYearsOfExperience,
    perks,
    visaSponsor,
    // Additional company fields (using new prefixed names to avoid collisions)
    companyRevenue,
    companyProfit,
    companyProfitMargin,
    companyMonthlyCounts,
    companyHiringTrends,
    companyHiringTrendsByLocale,
    registryRegistrationDate,
    originalJobLanguage,
    companyGeocoordinates,
    isFinland,
  } = displayJob as DisplayJob;

  const resolvedJobBody = resolveJobDisplayDescriptionFromDisplayJob({
    originalDescription,
    description,
  });
  const displayDescription = resolvedJobBody.body;

  // Use salary display hook
  const { displaySalary, convertedSalary, shouldShowOriginalButton } = useSalaryDisplay({
    displayJob,
    showOriginalSalary,
  });

  const finnishNetSalaryEstimate = useMemo(
    () => (isFinland ? estimateFinnishNetSalary(convertedSalary) : null),
    [isFinland, convertedSalary],
  );
  const finnishNetSalaryExplanationText = useMemo(() => {
    if (!finnishNetSalaryEstimate) return '';
    return formatMessage(
      {
        id: 'jobDetailPage.estimatedNetSalary.explanation',
        defaultMessage:
          'Estimated {year} take-home pay after Finnish income tax, pension, and unemployment insurance contributions. Assumes the national average municipal tax rate ({rate}%), no church tax, and standard deductions only; your actual net pay depends on your municipality, church membership, age, and family situation.',
        description: 'Explains the assumptions behind the estimated Finnish net (after-tax) salary figure shown near the gross compensation',
      },
      { year: finnishNetSalaryEstimate.taxYear, rate: finnishNetSalaryEstimate.assumedMunicipalRatePct },
    );
  }, [finnishNetSalaryEstimate, intl]);

  // Deep-links the editorial-rail redesign's "Est. take-home" figure to the tax iceberg
  // calculator, prefilled with this job's own salary range as three icebergs at once (low,
  // mid, high, in that order; same `compare` mechanism the calculator's own UI uses for
  // side-by-side comparison, drawn to the same scale) so a visitor sees the whole range's tax
  // picture instead of just one point. The low end is the primary (path) salary since the
  // primary iceberg always renders first, with mid/high riding along as `compare` query params
  // in the order they should appear. Gated the same way the estimate itself is
  // (finnishNetSalaryEstimate non-null implies isFinland + a usable convertedSalary), so this is
  // never built for a non-Finland job.
  const taxIcebergHref = useMemo(() => {
    if (!finnishNetSalaryEstimate || !convertedSalary) return null;
    const grossLow = Math.round(convertedSalary.min);
    const grossHigh = Math.round(convertedSalary.max);
    const grossMid = Math.round((grossLow + grossHigh) / 2);
    if (!(grossLow > 0)) return null;
    const compare = [grossMid, grossHigh].filter((amount) => amount > 0 && amount !== grossLow);
    return encodeTaxIcebergUrl({ ...DEFAULT_TAX_ICEBERG_STATE, gross: grossLow, compare }, jobLocale);
  }, [finnishNetSalaryEstimate, convertedSalary, jobLocale]);

  const salaryListingProvenance = resolveJobSalaryListingProvenance({
    salaryRangeMin,
    salaryRangeMax,
    salaryDetails,
  });

  // Get trends for current locale, fallback to English if not available
  const currentLocaleTrends = companyHiringTrendsByLocale?.[jobLocale as 'en' | 'fi' | 'sv']
    || companyHiringTrendsByLocale?.en
    || companyHiringTrends;

  // Use server-side calculated hasAnyCharts to prevent CLS (Cumulative Layout Shift)
  // This value is calculated in +data.ts before the page renders. In the job island, serialized data
  // might omit it, so fallback to computing from job so charts always show in the final version.
  const hasAnyCharts =
    serverHasAnyCharts ??
    (job && typeof job === 'object'
      ? calculateHasAnyCharts(job as Parameters<typeof calculateHasAnyCharts>[0], jobLocale)
      : false);

  // Set default tab based on available data
  useEffect(() => {
    if (!chartsCollapsed) {
      const hasFinancial = hasAnyValidNumber(companyRevenue, companyProfit, companyProfitMargin);
      const hasHiring = !!(companyMonthlyCounts && Object.keys(companyMonthlyCounts).length > 0);
      const hasLocations = !!(currentLocaleTrends &&
        ((currentLocaleTrends.locationDistribution &&
          Object.keys(currentLocaleTrends.locationDistribution).length > 0 &&
          Object.values(currentLocaleTrends.locationDistribution).some(val => hasAnyValidNumber(val))) ||
          (currentLocaleTrends.remotenessDistribution &&
            (currentLocaleTrends.remotenessDistribution.remote > 0 ||
              currentLocaleTrends.remotenessDistribution.hybrid > 0 ||
              currentLocaleTrends.remotenessDistribution.onsite > 0))));
      const hasRoles = !!(currentLocaleTrends && (currentLocaleTrends.roleDistribution || currentLocaleTrends.roleLevelDistribution));

      // Check if current tab has data, if not switch to first available
      const currentTabHasData =
        (companyInfoActiveTab === 'financial' && hasFinancial) ||
        (companyInfoActiveTab === 'hiring' && hasHiring) ||
        (companyInfoActiveTab === 'roles' && hasRoles) ||
        (companyInfoActiveTab === 'locations' && hasLocations) ||
        (companyInfoActiveTab === 'languages' && !!(currentLocaleTrends?.languageRequirements &&
          Object.keys(currentLocaleTrends.languageRequirements).length > 0 &&
          Object.values(currentLocaleTrends.languageRequirements).some(val => hasAnyValidNumber(val)))) ||
        (companyInfoActiveTab === 'map' && !!companyGeocoordinates);

      if (!currentTabHasData) {
        if (hasFinancial) {
          setCompanyInfoActiveTab('financial');
        } else if (hasHiring) {
          setCompanyInfoActiveTab('hiring');
        } else if (hasLocations) {
          setCompanyInfoActiveTab('locations');
        } else if (hasRoles) {
          setCompanyInfoActiveTab('roles');
        }
      }
    }
  }, [chartsCollapsed, companyRevenue, companyProfit, companyProfitMargin, companyMonthlyCounts, currentLocaleTrends, companyGeocoordinates, companyInfoActiveTab]);

  // Use server-validated heroImage (only shown if it has optimal dimensions)
  const heroImage = serverHeroImage;

  // TEC-632: the real apply URL never reaches the client; /api/getjob hands back this internal
  // redirect path instead (see entry_aws_lambda_api.ts), which 302s to the real URL server-side
  // (UTM params and the Tyomarkkinatori locale swap are applied there now, not here).
  const applyUrl = applyRedirectPath || '';

  const canonicalLocale = intl.locale === 'fi' || intl.locale === 'sv' ? intl.locale : 'en';
  const canonicalSource = normalizationMeta?.canonicalJob || finalJob;

  // Use language URLs hook
  const languageUrls = useLanguageUrls({
    canonicalSource,
    canonicalLocale,
  });

  const canonicalUrlForSchema = canonicalSource
    ? languageUrls?.current
    : (typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : undefined);

  // Generate schema and badge status
  const { schema: structuredData, isContentComplete } = generateJobPostingSchema(
    displayJob,
    intl.locale || 'en',
    applyUrl,
    canonicalUrlForSchema,
    normalizationMeta?.schemaOverrides ?? {}
  );

  const formattedPostedDate = formatDate(postedDate, intl.locale);
  const formattedLastSeenDate = formatDate(lastSeen, intl.locale);
  const postedDaysAgo = calculateDaysAgo(postedDate);
  const lastSeenDaysAgo = calculateDaysAgo(lastSeen);
  const formattedApplicationDate = formatDate(applicationDeadline, intl.locale);
  const formattedEstimatedExpiryDate = estimatedExpiryDate ? formatDate(estimatedExpiryDate, intl.locale) : null;

  const jobBreadcrumbModel = useMemo(
    () =>
      resolveJobPageBreadcrumbModel({
        locale: jobLocale,
        location: displayJob.location,
        structuredLocation: structuredJobLocation,
      }),
    [jobLocale, displayJob.location, structuredJobLocation],
  );

  // Generate meta description and keywords (used only as a fallback inside generateJobPostingSchema)
  const metaDesc = generateMetaDescription(displayDescription);
  const jobKeywords = generateJobKeywords(
    intl.locale as 'fi' | 'en' | 'sv',
    title,
    companyId,
    location,
    companyIndustry,
    specialisations
  );

  // Calculate brand styles
  const brandStyles = React.useMemo(() => {
    return calculateBrandStyles({
      brandColour: brandColour,
      isTyomarkkinatori: displayJob?.isTyomarkkinatori,
    });
  }, [brandColour, displayJob?.isTyomarkkinatori]);

  // The language switcher lives here in +Page.tsx, above JobDetailPageV2, so it needs its own
  // copy of the same brand colour resolution as JobDetailPageV2.tsx's adjustedBrand for the pill
  // to match the rest of the page.
  const languageSwitcherAccentColor = useMemo(() => {
    const isTyomarkkinatoriJob = resolveSearchHitIsTyomarkkinatori(displayJob as unknown as Record<string, unknown>);
    return adjustBrandColor(resolveEffectiveBrandColour(brandColour, isTyomarkkinatoriJob)).panel;
  }, [displayJob, brandColour]);

  // Fold sign-in/feedback/report into the alert CTA's own card as a footer, so the page ends in
  // one card with one primary button instead of two stacked full-weight CTA blocks. Falls back to
  // the old standalone EngagementStrip card below for the cases JobDetailAlertCta itself won't
  // render (Tyomarkkinatori jobs, or the handful of listings with no role facet to build an alert
  // around), so those pages never lose their only sign-in/feedback/report entry point. Built as
  // an element here (rather than inline JSX at its render site) because it renders inside
  // JobDetailPageV2EditorialRail, above SimilarJobsSection.
  const jobDetailAlertCtaElement =
    displayJob && !displayJob.isTyomarkkinatori && jobDetailAlertRole ? (
      <JobDetailAlertCta
        targetUrl={jobDetailAlertTargetUrl}
        role={jobDetailAlertRole}
        primaryLocation={jobDetailAlertPrimaryLocation}
        accentColor={languageSwitcherAccentColor}
        engagementFooter={
          <EngagementStrip
            feedbackSource="job_detail"
            variant="minimal"
            locale={jobLocale}
            showReportButton
            feedbackMetadata={{
              jobId: String(displayJob.objectID),
              companyId: displayJob.companyId,
              title: displayJob.title ?? displayJob.normalisedTitle,
              locale: jobLocale,
            }}
          />
        }
      />
    ) : null;

  const {
    effectiveBrandColour,
    useBrandColor,
    savedIconStyle,
  } = brandStyles;

  // "Explore more": links to indexable hubs only (role, role x city, city, employer, employers in
  // the city, salary, category, remote), built server-side in +data.ts.
  const relatedPages = (data as { relatedPages?: HubCrossLink[] } | undefined)?.relatedPages ?? [];

  const expiredJobBackHref = breadcrumbSearchReturnHref ?? (jobLocale === 'en' ? '/' : `/${jobLocale}`);

  // A 410 (job still indexed but treated as expired) used to render as a permanent banner above
  // the breadcrumb; moved to a dismissable toast so a reader isn't stuck with a persistent bar of
  // chrome above content they may still want to read (the job page itself stays fully visible
  // either way, this is just advisory). duration: 0 keeps it on screen until the reader dismisses
  // it or clicks through, since "this listing may be gone" isn't the kind of thing that should
  // silently disappear after a few seconds.
  useEffect(() => {
    if (httpStatusCode !== 410) return;
    showToast(
      formatMessage({
        id: 'jobDetailPage.expired.toastMessage',
        defaultMessage: 'This listing may be too old or no longer active.',
        description: 'Toast shown on expired job pages',
      }),
      'info',
      0,
      {
        label: formatMessage({
          id: 'jobDetailPage.expired.backToSearch',
          defaultMessage: 'Back to search results',
          description: 'Link from expired job banner back to the jobs listing hub',
        }),
        onClick: () => {
          window.location.href = expiredJobBackHref;
        },
      },
    );
    // Only ever meant to fire once per page load, when the job is first known to be expired.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [httpStatusCode]);

  return (
    <div className="min-h-screen bg-[#F8F5F0] dark:bg-[#170F07]" suppressHydrationWarning>
      <div className="max-w-6xl mx-auto p-2 pt-2 pb-6 sm:p-6">
            <div className="mb-4 flex flex-row items-center justify-between gap-2 px-1 sm:gap-4 sm:px-0">
              {/* Mobile: location segment dropped so the title always has room next to the language pills (still present in the page's breadcrumb JSON-LD for SEO). */}
              <div className="min-w-0 flex-1 sm:hidden">
                <BreadcrumbNavigation
                  showHome={false}
                  singleLine
                  className="mb-0 min-w-0 px-0"
                  items={[
                    {
                      label: formatMessage({
                        id: 'breadcrumb.jobs',
                        defaultMessage: 'Jobs',
                        description:
                          'Breadcrumb link to the jobs listing hub',
                      }),
                      href: breadcrumbSearchReturnHref ?? jobBreadcrumbModel.jobsHref,
                    },
                    { label: title },
                  ]}
                  trackHomeClick={true}
                  trackData={{ jobId: displayJob?.objectID }}
                />
              </div>
              <div className="hidden min-w-0 flex-1 sm:block">
                <BreadcrumbNavigation
                  showHome={false}
                  singleLine
                  className="mb-0 min-w-0 px-0"
                  items={[
                    {
                      label: formatMessage({
                        id: 'breadcrumb.jobs',
                        defaultMessage: 'Jobs',
                        description:
                          'Breadcrumb link to the jobs listing hub',
                      }),
                      href: breadcrumbSearchReturnHref ?? jobBreadcrumbModel.jobsHref,
                    },
                    ...(jobBreadcrumbModel.locationHref && jobBreadcrumbModel.locationLabel
                      ? [
                          {
                            label: jobBreadcrumbModel.locationLabel,
                            href: jobBreadcrumbModel.locationHref,
                          },
                        ]
                      : []),
                    { label: title },
                  ]}
                  trackHomeClick={true}
                  trackData={{ jobId: displayJob?.objectID }}
                />
              </div>

              {languageUrls && (
                <LanguageSwitcher
                  languageUrls={languageUrls}
                  canonicalLocale={canonicalLocale}
                  originalJobLanguage={originalJobLanguage}
                  className="shrink-0 px-0"
                  accentColor={languageSwitcherAccentColor}
                />
              )}
            </div>

            {/* Main content area - core job entity block */}
            <JobDetailPageV2
              displayJob={displayJob}
              title={title}
              normalisedTitle={((displayJob as Record<string, unknown>)[`normalisedTitle_${jobLocale}`] as string | undefined) || normalisedTitle}
              companyId={companyId}
              location={structuredJobLocation ? formatStructuredJobLocationLine(structuredJobLocation) : getFirstConcreteLocationSegment(location) || undefined}
              locationForChart={Array.isArray(location) ? location : location ? [location] : undefined}
              remoteness={remoteness}
              remoteScopes={structuredJobLocation?.remoteScopeAsLocation ? [] : structuredRemoteScopes}
              field={field}
              jobType={jobType}
              normalisedRoleLevel={normalisedRoleLevel}
              requiredLanguages={requiredLanguages}
              peopleManagementTier={peopleManagementTier}
              educationLevel={displayJob.educationLevel}
              minimumYearsOfExperience={minimumYearsOfExperience}
              collectiveAgreementId={displayJob.collectiveAgreementId}
              collectiveAgreementName={displayJob.collectiveAgreementName}
              collectiveAgreementMatchType={displayJob.collectiveAgreementMatchType}
              collectiveAgreementReasoning={displayJob.collectiveAgreementReasoning}
              collectiveAgreementConfidence={displayJob.collectiveAgreementConfidence}
              jobLocale={jobLocale}
              displayDescription={displayDescription}
              overviewHeaderProps={{
                displayJob,
                isAuthenticated,
                isHydrated,
                matchScoreData,
                matchScoreMeta,
                onLoginClick: () => {
                  trackEvent({
                    name: 'auth_interaction',
                    params: { action: 'sign_in_click', surface: 'job_detail_match_donut', locale: intl.locale },
                  });
                  signIn();
                },
                isSaved,
                isApplied,
                isHidden,
                currentApplicationState,
                ellipsisButtonState,
                isMenuOpen,
                menuRef,
                effectiveBrandColour,
                useBrandColor,
                onSave: handleToggleSaved,
                onToggleHide: handleToggleHide,
                onShare: handleShare,
                onSetApplicationState: handleSetApplicationState,
                onToggleApplied: handleToggleApplied,
                onMenuToggle: () => setIsMenuOpen(!isMenuOpen),
              }}
              applyUrl={applyUrl}
              isApplied={isApplied}
              setAppliedJobState={setAppliedJobState}
              onTailorCv={handleOpenTailorCv}
              onAutofillApply={applyAutofillEnabled ? () => setShowAutofillApplyModal(true) : undefined}
              formattedPostedDate={formattedPostedDate}
              postedDaysAgo={postedDaysAgo}
              formattedLastSeenDate={formattedLastSeenDate}
              formattedApplicationDate={formattedApplicationDate}
              formattedEstimatedExpiryDate={formattedEstimatedExpiryDate}
              matchScoreData={matchScoreData}
              salaryRangeMin={salaryRangeMin}
              salaryRangeMax={salaryRangeMax}
              currency={currency}
              salaryPeriod={salaryPeriod}
              salaryListingProvenance={salaryListingProvenance}
              optionalLanguages={optionalLanguages}
              perks={perks}
              jobDetailsRef={jobDetailsRef}
              currentLocaleTrends={currentLocaleTrends}
              liveOpenRolesCount={liveOpenRolesCount ?? null}
              companyEmployerScore={companyEmployerScore ?? null}
              relatedPages={relatedPages}
              effectiveSimilarJobs={effectiveSimilarJobs}
              similarJobsSeeAllHref={similarJobsSeeAllHref}
              finnishNetSalaryEstimate={finnishNetSalaryEstimate}
              finnishNetSalaryExplanationText={finnishNetSalaryExplanationText}
              taxIcebergHref={taxIcebergHref}
              alertCta={jobDetailAlertCtaElement}
            />
          </div>

          {displayJob?.isTyomarkkinatori === true ? (
            <div className="max-w-6xl mx-auto px-2 sm:px-6">
              <TyomarkkinatoriDataSourceAttribution />
            </div>
          ) : null}

          {displayJob && (displayJob.isTyomarkkinatori || !jobDetailAlertRole) ? (
            <div className="max-w-6xl mx-auto px-2 sm:px-6 pb-4 w-full">
              <EngagementStrip
                feedbackSource="job_detail"
                variant="card"
                locale={jobLocale}
                showReportButton
                feedbackMetadata={{
                  jobId: String(displayJob.objectID),
                  companyId: displayJob.companyId,
                  title: displayJob.title ?? displayJob.normalisedTitle,
                  locale: jobLocale,
                }}
              />
            </div>
          ) : null}

          {displayJob && applyAutofillEnabled ? (
            <AutofillApplyModal
              job={displayJob}
              applyUrl={applyUrl}
              isOpen={showAutofillApplyModal}
              onClose={() => setShowAutofillApplyModal(false)}
            />
          ) : null}

          {displayJob ? (
            <TailorCvModal
              job={displayJob}
              applyUrl={applyUrl}
              isOpen={showTailorCvModal}
              onClose={() => setShowTailorCvModal(false)}
              initialTab={tailorModalInitialTab}
            />
          ) : null}


        </div>
    );
  };

export default JobDetailPage;